/**
 * Sign-out: clear BeHeard's session, then end Central's SSO session.
 *
 * ── THE CENTRAL CONTRACT (verified against Central's route source) ─────────────
 *
 * Central's RP-initiated logout is `GET /api/oidc/logout` (routes/api/oidc.ts)
 * with exactly two query parameters:
 *
 *   client_id                 'beheard'
 *   post_logout_redirect_uri  absolute URL; Central redirects there only when its
 *                             protocol+host match one of the client's registered
 *                             redirect URIs (utils/oidcRequestHygiene.ts
 *                             postLogoutRedirectAllowed), otherwise it answers
 *                             JSON `{ ok: true }` and does not redirect.
 *
 * It destroys the session named by Central's own `central_sid` cookie, so it is
 * a top-level NAVIGATION (the browser sends Central's first-party cookie), not a
 * fetch. There is no `id_token_hint`: nothing credential-like goes in the URL,
 * which matters because a URL ends up in access logs.
 *
 * BeHeard used to `fetch(…/api/oidc/logout, { method: 'POST' })`. Central has no
 * POST on that path, so the call 404'd, was swallowed, and the Central session
 * survived logout — the next "Sign in" went straight back in.
 *
 * ── ORDER ──────────────────────────────────────────────────────────────────────
 *
 * Local state is cleared FIRST and unconditionally: whatever Central does (down,
 * slow, refusing the redirect), the access token is already gone from this tab.
 * Only then does the browser leave for Central.
 *
 * The post-logout target is `<origin of OIDC_REDIRECT_URI>/login`. That origin is
 * registered with Central by definition (it is where Central sends the login
 * code), so the redirect is always accepted, even if the app is opened on a
 * different hostname.
 */
// The shared builder, not config/centralAuth: this module stays free of
// import-time browser globals so its contract can be tested in isolation.
import { buildOidcLogoutUrl } from '@levelup/central-auth';

export interface CentralLogoutInput {
  centralUrl: string;
  clientId: string;
  /** VITE_OIDC_REDIRECT_URI: its origin is the one Central has registered. */
  oidcRedirectUri: string;
  /** Base for resolving a relative redirect URI (window.location.origin). */
  currentOrigin: string;
}

/** The Central RP-initiated logout URL, or null when Central is not configured. */
export function centralLogoutUrl(input: CentralLogoutInput): string | null {
  if (!input.centralUrl) return null;
  const origin = new URL(input.oidcRedirectUri, input.currentOrigin).origin;
  return buildOidcLogoutUrl({
    centralUrl: input.centralUrl,
    clientId: input.clientId,
    postLogoutRedirectUri: `${origin}/login`,
  });
}

export interface LogoutDeps extends CentralLogoutInput {
  /** Drop the access token, cached API data and any in-flight OIDC state. */
  clearLocalSession: () => void;
  /** Top-level navigation (window.location.assign). */
  navigate: (url: string) => void;
}

/**
 * Clear locally, then hand the browser to Central's logout. Local clearing can
 * never be skipped by a Central problem, and a local problem never strands the
 * user signed in to Central.
 */
export function signOut(deps: LogoutDeps): void {
  let localError: unknown = null;
  try {
    deps.clearLocalSession();
  } catch (e) {
    localError = e;
  }

  let url: string | null = null;
  try {
    url = centralLogoutUrl(deps);
  } catch {
    url = null; // A malformed redirect URI must not keep the user on a half-cleared page.
  }
  if (url) deps.navigate(url);

  if (localError) throw localError;
}
